import dayjs from "dayjs";

import { formatCurrency } from "@/helpers/currency-helpers";
import ISalesProductPrice from "@/interfaces/sales-product-price.interfaces";

type Return = Partial<ISalesProductPrice> & {
  formattedStartDate: string;
  formattedEndDate: string;
  formattedPricePerEvent: string;
  formattedEventsIncludedMonthlyFee: string;
  formattedFeePerSalesConvertion: string;
  formattedFeePerSalesConvertionWithPlus: string;
  formattedFixedFeePerSalesConvertion: string;
  formattedFixedMonthlyFee: string;
  totalValue: string;
};

type Props = {
  salesProductPrice: ISalesProductPrice | null;
  additionalFees?: {
    totalPerEventValue?: number;
    totalFeePerSalesConvertionValue?: number;
    totalSalesConvertionValue?: number;
  };
};

function useSalesProductPriceData({ salesProductPrice, additionalFees = {} }: Props): Return {
  if (!salesProductPrice) {
    return {
      formattedStartDate: "-",
      formattedEndDate: "-",
      formattedPricePerEvent: formatCurrency(0),
      formattedEventsIncludedMonthlyFee: formatCurrency(0),
      formattedFeePerSalesConvertion: "0%",
      formattedFeePerSalesConvertionWithPlus: "+0%",
      formattedFixedFeePerSalesConvertion: formatCurrency(0),
      formattedFixedMonthlyFee: formatCurrency(0),
      totalValue: formatCurrency(0),
    };
  }

  const {
    startDate,
    endDate,
    pricePerEvent,
    eventsIncludedMonthlyFee,
    feePerSalesConvertion,
    fixedFeePerSalesConvertion,
    fixedMonthlyFee,
  } = salesProductPrice;

  const {
    totalPerEventValue = 0,
    totalFeePerSalesConvertionValue = 0,
    totalSalesConvertionValue = 0,
  } = additionalFees;

  const formattedStartDate = startDate ? dayjs(startDate).format("DD/MM/YYYY") : "-";
  const formattedEndDate = endDate ? dayjs(endDate).format("DD/MM/YYYY") : "-";
  const formattedPricePerEvent = formatCurrency(pricePerEvent || 0);
  const formattedEventsIncludedMonthlyFee = formatCurrency(eventsIncludedMonthlyFee || 0);
  const formattedFeePerSalesConvertion = `${feePerSalesConvertion}%`;
  const formattedFeePerSalesConvertionWithPlus = `+${feePerSalesConvertion}%`;
  const formattedFixedFeePerSalesConvertion = formatCurrency(fixedFeePerSalesConvertion || 0);
  const formattedFixedMonthlyFee = formatCurrency(fixedMonthlyFee || 0);

  const totalValue = formatCurrency(
    (fixedMonthlyFee || 0) +
      totalPerEventValue +
      totalFeePerSalesConvertionValue +
      totalSalesConvertionValue
  );

  return {
    ...salesProductPrice,
    formattedStartDate,
    formattedEndDate,
    formattedPricePerEvent,
    formattedEventsIncludedMonthlyFee,
    formattedFeePerSalesConvertion,
    formattedFeePerSalesConvertionWithPlus,
    formattedFixedFeePerSalesConvertion,
    formattedFixedMonthlyFee,
    totalValue,
  };
}

export default useSalesProductPriceData;
